import { useTheme } from '@mui/material/styles';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { DashboardChartCard } from '../../../common/components';
import { materialCompactMoney, materialMoney } from '../../../utils/materialDisplay';
import { CHART_TICK_FONT_SIZE, MATERIAL_TEXTS } from '../constants/materialDashboardConstants';
import { truncateChartLabel } from '../utils/truncateChartLabel';

const BAR_RADIUS = [4, 4, 0, 0];

/**
 * Gráficos navegables por vivienda y por proveedor; un clic aplica el filtro y cambia de vista.
 * @param {Object} props
 * @param {Array<Object>} [props.projects] Costos por vivienda.
 * @param {Array<Object>} [props.providers] Atrasos por proveedor.
 * @param {boolean} props.isLoading Resumen en carga.
 * @param {Function} props.onProject Recibe la vivienda clickeada.
 * @param {Function} props.onProvider Recibe el proveedor clickeado.
 * @returns {JSX.Element}
 */
const MaterialDashboardCharts = ({
  projects = [],
  providers = [],
  isLoading,
  onProject,
  onProvider,
}) => {
  const theme = useTheme();
  const tick = { fill: theme.palette.text.secondary, fontSize: CHART_TICK_FONT_SIZE };
  const xAxisProps = { dataKey: 'name', tick, tickFormatter: (value) => truncateChartLabel(value) };

  return (
    <>
      <DashboardChartCard
        variant="plain"
        title={MATERIAL_TEXTS.materialsByProject}
        loading={isLoading}
        hasData={projects.length > 0}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={projects}
            margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
            onClick={(event) => onProject(event?.activePayload?.[0]?.payload)}
            style={{ cursor: 'pointer' }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis {...xAxisProps} />
            <YAxis tickFormatter={materialCompactMoney} tick={tick} />
            <RechartsTooltip formatter={materialMoney} />
            <Legend />
            <Bar
              dataKey="delivered_amount"
              stackId="cost"
              name={MATERIAL_TEXTS.delivered}
              fill={theme.palette.primary.main}
              isAnimationActive={false}
            />
            <Bar
              dataKey="reserved_amount"
              stackId="cost"
              name={MATERIAL_TEXTS.reservedEstimated}
              fill={theme.palette.info.main}
              isAnimationActive={false}
            />
            <Bar
              dataKey="pending_amount"
              stackId="cost"
              name={MATERIAL_TEXTS.toArrive}
              fill={theme.palette.warning.main}
              radius={BAR_RADIUS}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </DashboardChartCard>

      <DashboardChartCard
        variant="plain"
        title={MATERIAL_TEXTS.providerDelays}
        loading={isLoading}
        hasData={providers.length > 0}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={providers}
            margin={{ top: 10, right: 20, left: 0, bottom: 5 }}
            onClick={(event) => onProvider(event?.activePayload?.[0]?.payload)}
            style={{ cursor: 'pointer' }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis {...xAxisProps} />
            <YAxis allowDecimals={false} tick={tick} />
            <RechartsTooltip />
            <Legend />
            <Bar
              dataKey="overdue_count"
              stackId="delay"
              name={MATERIAL_TEXTS.overdueCount}
              fill={theme.palette.error.main}
              isAnimationActive={false}
            />
            <Bar
              dataKey="late_count"
              stackId="delay"
              name={MATERIAL_TEXTS.lateCount}
              fill={theme.palette.warning.main}
              radius={BAR_RADIUS}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </DashboardChartCard>
    </>
  );
};

export default MaterialDashboardCharts;
